import { api } from '../api';
import type { PortalConfig } from '../config';
import { AsyncBoundary, Card, Hero, Metric, Notice, SeverityCounts, formatDate, useAsync } from '../ui';

const CODE_SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL', 'UNTRIAGED'];
const RISK_LEVELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL', 'UNKNOWN'];

export function OverviewView({ config }: { config: PortalConfig }) {
  const state = useAsync(() => api.overview(config), [config]);

  return (
    <AsyncBoundary state={state}>
      {(overview) => (
        <>
          <Hero title="Security testing across every GTM application">
            Amazon Inspector scans the code in {overview.githubOrg} on every pull request, and AWS Security Agent
            attacks the running sandbox environments. Everything either service has found is below.
          </Hero>

          {overview.degraded.length > 0 ? (
            <Notice kind="warn">
              Some data could not be loaded ({overview.degraded.join(', ')}). The figures below are incomplete.
            </Notice>
          ) : null}

          {/* No active GitHub integration means pull request scanning has silently
              stopped, so it is called out before anything else. Scanning needs only
              one live connection, and abandoned attempts are left behind as PENDING
              integrations, so this asks whether any is active rather than whether
              all of them are. */}
          {!overview.codeScanning.integrations.some((integration) => integration.status === 'ACTIVE') ? (
            <Notice kind="error">
              No Amazon Inspector GitHub integration is active, so pull requests are not being scanned. Re-authorise it
              with scripts/connect-github-integration.sh, or connect the organisation in the Inspector console if there
              is no integration yet.
            </Notice>
          ) : null}

          <div className="grid section">
            <Card title="Repositories scanned">
              <Metric
                value={overview.codeScanning.repositoriesOnboarded}
                note={`${overview.codeScanning.repositoriesWithPullRequestScanning} with pull request scanning`}
              />
            </Card>

            <Card title="Open code findings">
              <Metric
                value={overview.codeScanning.totalFindings}
                note={Object.entries(overview.codeScanning.byCategory)
                  .map(([category, count]) => `${count} ${category}`)
                  .join(' · ')}
              />
            </Card>

            <Card title="Apps pen tested">
              <Metric
                value={overview.penetrationTesting.appsOnboarded}
                note={
                  overview.penetrationTesting.appsNeedingAttention > 0
                    ? `${overview.penetrationTesting.appsNeedingAttention} app(s) not fully onboarded — see the Apps tab`
                    : `${overview.penetrationTesting.pentestsConfigured} configurations · ${overview.penetrationTesting.runsInProgress} running`
                }
              />
            </Card>

            <Card title="Pen test findings">
              <Metric
                value={overview.penetrationTesting.totalFindings}
                note={
                  overview.penetrationTesting.neverRun > 0
                    ? `${overview.penetrationTesting.neverRun} configuration(s) never run`
                    : 'From the latest run of each test'
                }
              />
            </Card>
          </div>

          <div className="grid section">
            <Card title="Code findings by severity">
              <SeverityCounts counts={overview.codeScanning.bySeverity} order={CODE_SEVERITIES} />
            </Card>

            <Card title="Pen test findings by risk">
              <SeverityCounts counts={overview.penetrationTesting.byRiskLevel} order={RISK_LEVELS} />
            </Card>
          </div>

          <section className="section">
            <h2>Repositories with the most findings</h2>
            <div className="card">
              {overview.codeScanning.topRepositories.length === 0 ? (
                <p className="muted">No findings yet.</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Repository</th>
                      <th>Open findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.codeScanning.topRepositories.map((row) => (
                      <tr key={row.repository}>
                        <td>{row.repository}</td>
                        <td>{row.findings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <p className="muted section">
            {overview.githubOrg} · {overview.environment} · generated {formatDate(overview.generatedAt)}
          </p>
        </>
      )}
    </AsyncBoundary>
  );
}
